import { toast } from "../../../utils/toast";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  useParams,
  useNavigate,
  useLocation,
  matchPath,
} from "react-router-dom";
import { fetchChannel } from "../../../redux/slices/channelSlice";
import ProfileIcon from "../../../assets/icons/profile.svg";
import ColorProfile from "../../../assets/images/color_profile.svg";

import DropDown from "../../../assets/icons/arrow_drop_down.svg";
import DropDownLight from "../../../assets/lightIcons/arrow_drop_down_light.svg";
import { getAppPrefix } from "./../utility/embedHelper";
import { fetchChannels } from "./../../../redux/slices/channelItemsSlice";
import { fetchMyData } from "./../../../redux/slices/myDataSlice";
import useModal from "./../../hooks/ModalHook";

const EmbedHeaderPage = ({ overlay = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const location = useLocation();
  const { handleOpenModal } = useModal();

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  // const [channelsData, setChannelsData] = useState([]);
  const hasFetched = useRef(false);

  const [username, setUsername] = useState("");
  const [channelId, setChannelId] = useState("");
  const dispatch = useDispatch();

  const channel = useSelector((state) => state.channel);
  const myData = useSelector((state) => state.myData);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const channelsData = useSelector((state) => state.channelItems.channels);
  const branding = useSelector((state) => state.embedHome?.branding) || {};
  const features = useSelector((state) => state.embedHome?.features) || {};

  const channelName = channel.name;

  useEffect(() => {
    if (!myData._id) {
      dispatch(fetchMyData());
    }
  }, [myData._id]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = localStorage.getItem("embedFetchedData");
      if (data) {
        const dataId = JSON.parse(data);
        setUsername(dataId.username);
        setChannelId(dataId.selectedChannel);
        clearInterval(interval);
      }
    }, 200);

    return () => clearInterval(interval);
  }, []);

  // IMPORTANT: the header must NOT fetch a channel. The current page
  // (ChannelPage / PageHome) owns `state.channel` for whatever channel the
  // route points at. Fetching the *default* channel here raced with the page,
  // clobbering the selected channel back to the default — which made the top
  // bar show the wrong channel, broke membership/authorization, and doubled
  // every channel load. The title simply reads the page-managed `state.channel`.

  useEffect(() => {
    if (username && !hasFetched.current) {
      dispatch(fetchChannels(username))
        .unwrap()
        .then((channels) => {
          hasFetched.current = true;
        })
        .catch((error) => {
          toast(error, "error");
        });
    }
  }, [username, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  const toggleChannel = (channel) => {
    toggleDropdown();
    // Always land on the channel page — it resolves membership/topics reliably
    // (the dropdown's cached member list isn't trustworthy). The member then
    // opens a topic from there.
    navigate(`${getAppPrefix()}/user/${username}/channel/${channel._id}`);
  };

  const handleBrandProfile = () => {
    toggleDropdown();
    navigate(`${getAppPrefix()}/user/${username}/profile?view=brand`);
  };

  const handleMessagePage = () => {
    toggleDropdown();
    if (myData.username) {
      navigate(`${getAppPrefix()}/user/${myData.username}/messages/list`);
    }
  };

  const isTopicPage =
    matchPath(
      `${getAppPrefix()}/user/:username/channel/:channelId/c-id/topic/:topicId`,
      location.pathname
    ) ||
    matchPath(
      `${getAppPrefix()}/user/:username/channel/:channelId`,
      location.pathname
    );
  const isMessagePage = matchPath(
    `${getAppPrefix()}/user/:username/messages/*`,
    location.pathname
  );
  const isProfilePage = matchPath(
    `${getAppPrefix()}/user/:username/profile`,
    location.pathname
  );
  const handleLogout = () => {
    toggleDropdown();
    handleOpenModal("modalEmbedLogoutOpen");
  };

  const handleLogin = () => {
    const data = localStorage.getItem("embedFetchedData");
    const dataId = JSON.parse(data);
    const channelId = dataId.selectedChannel;
    const username = dataId.username;
    toggleDropdown();
    navigate(
      `${getAppPrefix()}/get-started?redirect=${getAppPrefix()}/user/${username}/channel/${channelId}`,
      {
        replace: true,
      }
    );
  };
  if (isProfilePage) return null;

  // In overlay mode the header floats over the channel cover image. The brand
  // identity sits inside a frosted "pill" so it stays legible over any cover.
  const arrowStyle = overlay ? { filter: "brightness(0) invert(1)" } : undefined;

  return (
    <div
      className={`flex flex-row py-2 justify-between items-center pl-4 pr-10 w-full h-12 ${
        overlay ? "bg-transparent" : "bg-theme-secondaryBackground"
      }`}
    >
      <div
        className={`flex-row items-center flex relative ${
          overlay
            ? "rounded-full bg-black/45 backdrop-blur-md pl-2.5 pr-1.5 py-1 shadow-lg shadow-black/30"
            : ""
        }`}
        ref={dropdownRef}
      >
        {branding.logo && (
          <img
            src={branding.logo}
            alt="brand"
            className={`mr-2 rounded object-cover ${
              overlay ? "h-6 w-6" : "h-7 w-7"
            }`}
          />
        )}
        <p
          className={`flex font-medium font-inter ${
            overlay
              ? "text-white text-base sm:text-lg drop-shadow"
              : "text-theme-secondaryText text-base sm:text-lg"
          }`}
        >
          {(() => {
            const rawText = isTopicPage
              ? channelName.charAt(0).toUpperCase() + channelName.slice(1)
              : isMessagePage
              ? "Messages"
              : branding.displayName || "Channels";

            return rawText.length > 40 ? `${rawText.slice(0, 40)}...` : rawText;
          })()}
        </p>

        <img
          src={DropDown}
          alt="arrow-right"
          style={arrowStyle}
          className={`w-6 h-6 ml-0.5 cursor-pointer ${
            overlay ? "block" : "dark:block hidden"
          }`}
          onClick={toggleDropdown}
        />
        {!overlay && (
          <img
            src={DropDownLight}
            alt="arrow-right"
            className="dark:hidden w-6 h-6 ml-0.5 cursor-pointer "
            onClick={toggleDropdown}
          />
        )}

        {isDropdownOpen && (
          <div
            className="absolute left-0 top-9 z-50 w-64 overflow-hidden rounded-xl border border-theme-chatDivider bg-theme-tertiaryBackground shadow-xl"
            role="menu"
            aria-orientation="vertical"
          >
            {(() => {
              const base = channelsData.filter(
                (c) =>
                  isMessagePage || isProfilePage || c.name !== channelName
              );
              // Full switcher on -> show all. Switcher off but "show public
              // channels" on -> show only public ("anyone") channels. Both off
              // -> show none.
              const others =
                features.channelSwitcher !== false
                  ? base
                  : features.showPublicChannels
                  ? base.filter((c) => c.visibility === "anyone")
                  : [];
              return others.length > 0 ? (
                <div className="border-b border-theme-chatDivider py-2">
                  <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-theme-placeholder">
                    Switch channel
                  </p>
                  <div className="max-h-56 overflow-y-auto custom-scrollbar">
                    {others.map((channel) => (
                      <button
                        key={channel._id}
                        onClick={() => toggleChannel(channel)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-light text-theme-primaryText hover:bg-theme-sidebarHighlight"
                      >
                        <span className="text-theme-placeholder">#</span>
                        <span className="truncate">{channel.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            <div className="py-2">
              <button
                onClick={handleBrandProfile}
                className="flex w-full items-center px-4 py-2 text-left text-sm font-light text-theme-primaryText hover:bg-theme-sidebarHighlight"
              >
                Brand profile
              </button>
              {features.dms !== false && (
                <button
                  onClick={handleMessagePage}
                  className="flex w-full items-center px-4 py-2 text-left text-sm font-light text-theme-primaryText hover:bg-theme-sidebarHighlight"
                >
                  Messages
                </button>
              )}
            </div>

            <div className="border-t border-theme-chatDivider py-2">
              {isLoggedIn ? (
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center px-4 py-2 text-left text-sm font-light text-theme-error hover:bg-theme-sidebarHighlight"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={handleLogin}
                  className="flex w-full items-center px-4 py-2 text-left text-sm font-light text-theme-primaryText hover:bg-theme-sidebarHighlight"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {myData?.username && (
        <div className="relative" ref={profileRef}>
          <button
            className={`rounded-full h-8 w-8 flex cursor-pointer ${
              overlay ? "ring-2 ring-white/50 shadow-lg shadow-black/30" : ""
            }`}
            onClick={() => setProfileOpen((v) => !v)}
            aria-label="Your profile"
          >
            {myData?.logo ? (
              <img
                src={myData.logo}
                alt="profile"
                className="rounded-full w-full h-full object-cover shrink-0"
              />
            ) : myData?.color_logo ? (
              <div
                className="rounded-full w-full h-full shrink-0 flex items-center justify-center"
                style={{ backgroundColor: myData?.color_logo }}
              >
                <img src={ColorProfile} alt="" className="w-4 h-4" />
              </div>
            ) : (
              <img
                src={ProfileIcon}
                alt="profile"
                className="rounded-full w-full h-full object-cover shrink-0"
              />
            )}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-10 z-50 w-56 overflow-hidden rounded-md border border-theme-chatDivider bg-theme-tertiaryBackground shadow-lg">
              <div className="flex items-center gap-3 border-b border-theme-chatDivider px-3 py-3">
                {myData?.logo ? (
                  <img
                    src={myData.logo}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-full"
                    style={{ backgroundColor: myData?.color_logo || "#888" }}
                  >
                    <img src={ColorProfile} alt="" className="w-4 h-4" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-theme-secondaryText">
                    {myData?.name || myData?.username}
                  </p>
                  {myData?.email && (
                    <p className="truncate text-xs text-theme-placeholder">
                      {myData.email}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigate(`/embed/channels/user/${myData?.username}/profile`);
                }}
                className="block w-full px-3 py-2 text-left text-sm text-theme-primaryText hover:bg-theme-sidebarHighlight"
              >
                View profile
              </button>
              {isLoggedIn && (
                <button
                  onClick={() => {
                    setProfileOpen(false);
                    handleLogout();
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-theme-primaryText hover:bg-theme-sidebarHighlight"
                >
                  Logout
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmbedHeaderPage;
